import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Save, Upload, Eye, CheckCircle2, AlertCircle, Phone, MapPin, Mail } from "lucide-react";
import { tplApi } from "@/lib/templateApi";

export default function ContactEditor() {
  const { tenant, slug } = useOutletContext();
  const theme = tenant?.theme || {};
  const primary = theme.primary_color || "#1E3A5F";

  const [form, setForm] = useState({ address: "", phone: "", email: "", google_map_embed: "", form_enabled: true });
  const [published, setPublished] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await tplApi.getAdminContent(slug);
      const m = res.modules?.contact;
      if (m) { const src = m.draft || m.published; if (src) setForm(src.content_json); if (m.published) setPublished(m.published.content_json); }
    } catch {}
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const flash = (msg, type = "success", link = null) => { setToast({ msg, type, link }); setTimeout(() => setToast(null), 5000); };
  const save = async (action) => {
    setSaving(true);
    try { await tplApi.saveContent(slug, "contact", form, action); flash(action === "publish" ? "Published!" : "Draft saved.", "success", action === "publish" ? `/t/${slug}` : null); if (action === "publish") setPublished({ ...form }); }
    catch (e) { flash(e.message, "error"); } finally { setSaving(false); }
  };
  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const fields = [
    { key: "address", label: "Address", help: "Your full street address.", type: "text", icon: MapPin },
    { key: "phone", label: "Phone Number", help: "Main business phone number.", type: "tel", icon: Phone },
    { key: "email", label: "Email", help: "Where customer inquiries should go.", type: "email", icon: Mail },
    { key: "google_map_embed", label: "Google Maps Embed URL", help: "Go to Google Maps → Share → Embed → copy the src URL from the iframe.", type: "url" },
  ];

  return (
    <div className="flex flex-col lg:flex-row h-full" data-testid="contact-editor">
      <div className="lg:w-[440px] shrink-0 border-r border-white/[0.06] overflow-auto">
        <div className="p-6 border-b border-white/[0.06]">
          <h2 className="text-lg font-bold text-white">Contact Info</h2>
          <p className="text-slate-500 text-xs mt-0.5">How customers can reach you.</p>
        </div>
        <div className="p-6 space-y-5">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-sm font-medium text-slate-300 block mb-1">{f.label}</label>
              <Input value={form[f.key] || ""} onChange={(e) => up(f.key, e.target.value)} type={f.type}
                className="bg-[#1E293B] border-white/10 text-white h-10 rounded-lg focus:ring-orange-500" data-testid={`contact-field-${f.key}`} />
              <p className="text-[11px] text-slate-600 mt-1">{f.help}</p>
            </div>
          ))}
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={form.form_enabled !== false} onChange={(e) => up("form_enabled", e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-[#1E293B]" data-testid="contact-form-toggle" />
            <label className="text-sm text-slate-300">Show contact form on public site</label>
          </div>
        </div>
        <div className="p-6 border-t border-white/[0.06] flex gap-3 sticky bottom-0 bg-[#0F172A]">
          <button onClick={() => save("draft")} disabled={saving} className="flex-1 bg-[#1E293B] border border-white/10 text-slate-300 hover:text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-draft-btn"><Save size={15} /> Save Draft</button>
          <button onClick={() => save("publish")} disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="publish-btn"><Upload size={15} /> Publish</button>
        </div>
      </div>

      <div className="flex-1 bg-[#0B1120] overflow-auto" data-testid="contact-preview-panel">
        <div className="p-4 border-b border-white/[0.06] flex items-center gap-2"><Eye size={14} className="text-slate-500" /><span className="text-xs text-slate-500 font-medium">Live Preview</span></div>
        <div className="p-6">
          <div className="rounded-xl overflow-hidden border border-white/[0.06] bg-white" style={{ maxWidth: 800 }}>
            <div className="py-12 px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Get in Touch</h2>
                <div className="space-y-3 text-sm text-gray-600">
                  {form.address && <div className="flex items-start gap-2"><MapPin size={15} style={{ color: primary }} className="mt-0.5 shrink-0" /><span>{form.address}</span></div>}
                  {form.phone && <div className="flex items-center gap-2"><Phone size={15} style={{ color: primary }} /><span>{form.phone}</span></div>}
                  {form.email && <div className="flex items-center gap-2"><Mail size={15} style={{ color: primary }} /><span>{form.email}</span></div>}
                </div>
                {form.google_map_embed && <div className="mt-4 rounded-lg overflow-hidden h-36 bg-gray-100"><iframe src={form.google_map_embed} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title="Map" /></div>}
              </div>
              {form.form_enabled !== false && (
                <div className="bg-gray-50 rounded-lg p-5">
                  <h3 className="font-semibold text-gray-900 text-sm mb-3">Send us a message</h3>
                  <div className="space-y-2">
                    <div className="h-9 bg-white border border-gray-200 rounded-md" />
                    <div className="h-9 bg-white border border-gray-200 rounded-md" />
                    <div className="h-20 bg-white border border-gray-200 rounded-md" />
                    <div className="h-9 rounded-md text-white text-xs font-semibold flex items-center justify-center" style={{ background: primary }}>Send Message</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {toast && <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-xl flex items-center gap-2 text-sm font-medium z-50 ${toast.type === "error" ? "bg-red-900/90 text-red-200 border border-red-800/50" : "bg-emerald-900/90 text-emerald-200 border border-emerald-800/50"}`}>{toast.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}{toast.msg}{toast.link && <a href={toast.link} target="_blank" rel="noopener noreferrer" className="underline ml-1">View site &rarr;</a>}</div>}
    </div>
  );
}
